import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import {currentBranding} from '../../../config/branding';
import { branding, documentMimeTypes, imageMimeTypes, videoMimeTypes } from '../../../config/enum';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../../interface/file-upload-stream.interface';
import { StationDetails, Amenity } from '../../../interface/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../redux/slice/FileUploadStreamSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { fetchPendingStations } from '../../../redux/slice/PendingStationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomAddressInput from '../../../shared-components/custom-address-input/CustomAddressInput';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../../../shared-components/custom-media-upload/CustomMediaUpload';
import CustomTimeRange from '../../../shared-components/custom-time-input/CustomTimeRange';
import FileUploadProgress from '../../../shared-components/file-upload-progress/FileUploadProgress';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SelectLocation from '../../../shared-components/select-location/SelectLocation';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import { SanitizeFileName } from '../../../shared-functions/StringFunctions';
import {
  checkFileSizeValidation,
  longTextValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import station from '../../../utils/api/station';

import SelectAmenities from './SelectAmenities';


interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add new station component
 */
function AddStation() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.pendingStation);
  const [stationDetails, setStationDetails] = useState<StationDetails>({
    name: '',
    landOwnerShipDocument: [],
    ebReport: [],
    electricityContractType: '',
    opensAt: '00:00',
    closesAt: '23:59',
    address: '',
    latitude: null,
    longitude: null,
    medias: [],
    amenities: [],
    electricityCostByUtilityProvider: null,
    isGuestChargingExclusive: false,
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const listOfCountryCode = [
    {
      label: 'Standard Residential',
      value: 'standard-residential',
    },
    {
      label: 'Commercial',
      value: 'commercial',
    },
    {
      label: 'Industrial',
      value: 'industrial',
    },
    {
      label: 'EV Charging-Specific Contracts',
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle input value change
   * @param name
   * @param changeValue
   */
  const handleStationDetailChange = (
    name: string,
    changeValue: string | number | File[] | Amenity[] | boolean,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: changeValue,
    });
  };

  /**
   * Handle Change address
   */
  const handleChangeAddress = (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: address,
      latitude,
      longitude,
    });
  };

  /**
   * Handle lat & long change
   * @param changeValue
   */
  const handleChangeLatLong = (latitude: number, longitude: number) => {
    setStationDetails((previousValue) => ({
      ...previousValue,
      latitude,
      longitude,
    }));
  };

  /**
   * View amenities popup
   */
  const openAmenitiesModal = () => {
    setModalBoxDetail({
      title: 'Amenities',
      content: (
        <SelectAmenities
          selectedAmenities={stationDetails.amenities}
          handleCancel={() => setModalBoxStatus('close')}
          handleStationDetailChange={handleStationDetailChange}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  /**
   * Function to get and handle file upload progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setStationDetails({
      name: '',
      landOwnerShipDocument: [],
      ebReport: [],
      electricityContractType: '',
      opensAt: '',
      closesAt: '',
      address: '',
      latitude: null,
      longitude: null,
      medias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
    });
    setModalBoxStatus('close');

    navigate('../approved');
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title="Are you sure want to Cancel?"
          description="Any unsaved changes will be loss"
          cancelButtonLabel="Cancel"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel="OK"
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Add station API call
   */
  const handleSubmitStationDetails = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (
      stringValidation(1, 100, stationDetails.name) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.landOwnerShipDocument &&
          stationDetails.landOwnerShipDocument.length > 0 &&
          checkFileSizeValidation(stationDetails.landOwnerShipDocument, 5, 1, 0, [
            ...imageMimeTypes,
            ...documentMimeTypes,
          ]))) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.ebReport &&
          stationDetails.ebReport.length > 0 &&
          checkFileSizeValidation(stationDetails.ebReport, 5, 1, 0, documentMimeTypes))) &&
      stationDetails.electricityContractType &&
      stationDetails.opensAt &&
      stationDetails.closesAt &&
      stationDetails.opensAt < stationDetails.closesAt &&
      longTextValidation(500, stationDetails.address) &&
      stationDetails.latitude &&
      stationDetails.longitude &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.medias &&
          stationDetails.medias.length > 0 &&
          checkFileSizeValidation(stationDetails.medias, 10, 10, 100, [
            ...imageMimeTypes,
            ...videoMimeTypes,
          ]))) &&
      stationDetails.electricityCostByUtilityProvider &&
      stationDetails.electricityCostByUtilityProvider > 0
      // stationDetails.amenities.length > 0
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', stationDetails.name);
      formData.append('electricityContractType', stationDetails.electricityContractType);
      formData.append('opensAt', ConvertToUTC(stationDetails.opensAt));
      formData.append('closesAt', ConvertToUTC(stationDetails.closesAt));
      formData.append('address', stationDetails.address);
      formData.append(
        'electricityCostByUtilityProvider',
        stationDetails.electricityCostByUtilityProvider.toString(),
      );
      formData.append(
        'isGuestChargingExclusive',
        stationDetails.isGuestChargingExclusive.toString(),
      );
      formData.append(
        'latitude',
        stationDetails.latitude ? stationDetails.latitude.toString() : '',
      );

      formData.append(
        'longitude',
        stationDetails.longitude ? stationDetails.longitude.toString() : '',
      );

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(stationDetails.landOwnerShipDocument)) {
        stationDetails.landOwnerShipDocument.forEach((file) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(stationDetails.ebReport)) {
        stationDetails.ebReport.forEach((file) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append multiple files for medias (if it's an array)
      if (stationDetails.medias && Array.isArray(stationDetails.medias)) {
        stationDetails.medias.forEach((media) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (stationDetails.amenities && Array.isArray(stationDetails.amenities)) {
        stationDetails.amenities.forEach((amenity, index) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      const binaryFileData: SingleFileUploadProgress[] = [
        ...stationDetails.landOwnerShipDocument,
        ...stationDetails.medias,
        ...stationDetails.ebReport,
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: 'File',
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      station
        .addStation(formData, handleUploadProgress)
        .then(() => {
          dispatch(setUnUploadedFileData([]));
          dispatch(fetchPendingStations({ page, limit }));
          setModalBoxStatus('open');
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title="Station Added Successfully "
                description="Your Station is saved successfully"
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('close');
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  return (
    <div className="add-station-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-station-header">
        <PageTitle title="Add Station" />
      </div>
      <div className="add-station-container">
        <div className="add-station-from-wrap">
          <div className="add-station-from">
            <CustomInput
              inputLabel="Station Name"
              type="email"
              placeholder="Enter the Name"
              name="name"
              value={stationDetails.name}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={!stringValidation(1, 100, stationDetails.name) && formStatus.showFormErrors}
              errorMessage="Station name is required"
            />
            <CustomFileInput
              inputLabel="Land Ownership Document"
              acceptedFileType="image/*, application/pdf"
              placeholder="Upload pdf, jpeg"
              name="landOwnerShipDocument"
              selectedFiles={stationDetails.landOwnerShipDocument}
              onChangeValue={handleStationDetailChange}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                currentBranding !== branding.VOLTIE &&
                (!stationDetails.landOwnerShipDocument ||
                  stationDetails.landOwnerShipDocument.length === 0 ||
                  !checkFileSizeValidation(stationDetails.landOwnerShipDocument, 5, 1, 0, [
                    ...imageMimeTypes,
                    ...documentMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
            />
            <CustomFileInput
              inputLabel="EB Report of past 6 months"
              acceptedFileType="application/pdf"
              placeholder="Upload pdf"
              name="ebReport"
              selectedFiles={stationDetails.ebReport}
              onChangeValue={handleStationDetailChange}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                currentBranding !== branding.VOLTIE &&
                (!stationDetails.ebReport ||
                  stationDetails.ebReport.length === 0 ||
                  !checkFileSizeValidation(stationDetails.ebReport, 5, 1, 0, [
                    ...documentMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
            />
            <SelectFiled
              inputLabel="Electricity Contract type"
              name="electricityContractType"
              placeholder="Choose Electricity Contract type"
              selectedValue={stationDetails.electricityContractType}
              onChangeValue={handleStationDetailChange}
              options={listOfCountryCode}
              valueRequired
              error={!stationDetails.electricityContractType && formStatus.showFormErrors}
              errorMessage="Electricity contract type is required"
            />
            <CustomAddressInput
              inputLabel="Address"
              placeholder="Enter the address"
              name="address"
              value={stationDetails.address}
              onChangeValue={handleChangeAddress}
              valueRequired
              error={!longTextValidation(500, stationDetails.address) && formStatus.showFormErrors}
              errorMessage="Address is required"
            />
            <SelectLocation
              inputLabel="Latitude & Longitude"
              latitude={stationDetails.latitude}
              longitude={stationDetails.longitude}
              onChangeValue={handleChangeLatLong}
              valueRequired
              error={
                !(stationDetails.latitude && stationDetails.longitude) && formStatus.showFormErrors
              }
              errorMessage="Latitude and Longitude is required"
            />
            <CustomTimeRange
              name1="opensAt"
              name2="closesAt"
              inputLabel="Operating Hours"
              value1={stationDetails.opensAt}
              value2={stationDetails.closesAt}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={
                !(
                  stationDetails.opensAt &&
                  stationDetails.closesAt &&
                  stationDetails.opensAt < stationDetails.closesAt
                ) && formStatus.showFormErrors
              }
              // errorMessage="Invalid time range"
            />
            <CustomMediaUpload
              inputLabel="Upload Media"
              acceptedFileType=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.mkv"
              placeholder="Upload jpeg, png, mp4, avi"
              name="medias"
              selectedFiles={stationDetails.medias}
              valueRequired={currentBranding !== branding.VOLTIE}
              onChangeValue={handleStationDetailChange}
              error={
                currentBranding !== branding.VOLTIE &&
                (stationDetails.medias?.length === 0 ||
                  !checkFileSizeValidation(stationDetails.medias, 10, 10, 100, [
                    ...imageMimeTypes,
                    ...videoMimeTypes,
                  ])) &&
                formStatus.showFormErrors
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
            />
            <CustomInput
              inputLabel="Usage Cost (per Kwh)"
              type="number"
              placeholder="Enter Usage Cost"
              name="electricityCostByUtilityProvider"
              value={stationDetails.electricityCostByUtilityProvider}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={
                !(
                  stationDetails.electricityCostByUtilityProvider &&
                  stationDetails.electricityCostByUtilityProvider > 0
                ) && formStatus.showFormErrors
              }
              errorMessage="Usage Cost is required"
            />
            <CustomInputSwitch
              inputLabel="Exclusive for guest charging"
              value={stationDetails.isGuestChargingExclusive}
              name="isGuestChargingExclusive"
              onChangeValue={handleStationDetailChange}
            />
          </div>
        </div>
        <div className="add-station-button-wrap">
          <div className="amenities-button">
            <MyButton
              label="Amenities"
              svgIcon={<PlusIcon />}
              // buttonType={
              //   formStatus.showFormErrors && stationDetails.amenities.length === 0
              //     ? 'error'
              //     : 'back'
              // }
              buttonType="back"
              disableHoverEffect
              onClickFunc={openAmenitiesModal}
              notifyCount={stationDetails.amenities.length}
            />
          </div>
          <div className="form-buttons-wrap">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label="Submit"
              buttonType="submit"
              onClickFunc={handleSubmitStationDetails}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddStation;
